/**
 * Layout Component - Application shell with sidebar and header
 *
 * Features:
 * - Fixed sidebar with navigation
 * - Integration status pills
 * - User profile section
 * - Page header with theme toggle
 */

import { Outlet } from 'react-router-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {DashboardIcon, GitHubLogo, JiraLogo, ProjectsIcon, SettingIcon} from './icons';
import {
  LogOut,
  Zap,
  UserRound,
} from 'lucide-react';
import { useIntegrationStatuses } from '../hooks/useIntegrationStatuses';
import ThemeToggle from './ThemeToggle';

interface Integration {
  id: 'github' | 'jira';
  name: string;
  connected: boolean;
}

interface SidebarUserProfileProps {
  name: string | null | undefined;
  tenantName: string | null | undefined;
  onSignOut: () => void;
}

/** Avatar, name, tenant, and sign-out control shown at the bottom of the sidebar. */
function SidebarUserProfile({ name, tenantName, onSignOut }: SidebarUserProfileProps) {
  return (
    <div className="flex justify-between w-full items-center pl-6 pr-4 h-10">
      <div className="flex gap-3 items-center min-w-0">
        <div className="w-10 h-10 rounded-full bg-primary-DEFAULT flex items-center justify-center shrink-0">
          <span className="text-white font-semibold text-sm">
            <UserRound />
          </span>
        </div>
        <div className="min-w-0">
          <p className="font-body-base text-gray-900 dark:text-white truncate">{name ?? 'User'}</p>
          {tenantName && (
            <p className="font-caption text-gray-500 dark:text-gray-400 truncate">{tenantName}</p>
          )}
        </div>
      </div>
      <button onClick={onSignOut} className="cursor-pointer">
        <LogOut className="w-6 h-6 text-[#B3B3B3]" />
      </button>
    </div>
  );
}

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { githubStatus, jiraStatus } = useIntegrationStatuses();
  const isAdmin = user?.role === 'admin';

  const navigation = [
    { name: 'Dashboard', path: '/dashboard', icon: DashboardIcon },
    { name: 'Projects', path: '/projects', icon: ProjectsIcon },
    ...(isAdmin ? [{ name: 'Settings', path: '/settings', icon: SettingIcon }] : []),
  ];

  const integrations: Integration[] = [
    { id: 'github', name: 'GitHub', connected: githubStatus.status === 'connected' },
    { id: 'jira', name: 'Jira', connected: jiraStatus.status === 'connected' },
  ];

  const handleSignOut = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="main-container">
      {/* Sidebar */}
      <aside>
      <div className="sidebar flex flex-col">
        {/* Logo */}
        <div className="flex gap-3 items-center px-6 w-full">
           <div className="logo flex-center">
              <Zap className="w-[1.4rem] h-[1.56rem] text-white" strokeWidth={2} />
            </div>
            <span className="text-heading-sm font-medium text-gray-900 dark:text-white">Resolve AI</span>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto mt-9">
          <nav className="space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname.startsWith(item.path);
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex font-base items-center gap-3 px-8 py-5 transition-all ${
                    isActive
                      ? 'text-primary-DEFAULT dark:text-[#F5F5F5] bg-[#F5F5F5] dark:bg-[#444444]'
                      : 'text-info-DEFAULT '
                  }`}
                >
                  <Icon />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Integration status pills */}
        <div className="pl-6 pr-4 mb-6 space-y-3">
          {integrations.map((integration) => {
            const isConnected = integration.connected;
            return (
              <div
                key={integration.id}
                className={`w-full flex items-center gap-2 px-3 py-4 rounded-xl border transition-all border-[#D9D9D9] ${
                  isConnected
                    ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400'
                    : 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400'
                }`}
              >
                {/* Integration Logo */}
                <div className="shrink-0">
                  {integration.id === 'github' && (
                    <GitHubLogo className="w-4.5 h-4.5 text-gray-900 dark:text-white" />
                  )}
                  {integration.id === 'jira' && (
                    <JiraLogo className="w-4.5 h-4.5" />
                  )}
                </div>

                {/* Integration Info */}
                <div className="flex-1 min-w-0">
                  <p className={'font-body-sm text-[#1E1E1E] dark:text-white'}>
                    {integration.name}
                  </p>
                </div>

                {/* Status Indicator Dot */}
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${
                    isConnected
                      ? 'bg-success-DEFAULT'
                      : 'bg-error-DEFAULT'
                  }`} />
                  <p className={`font-caption`}>
                    {isConnected ? 'Connected' : 'Not Connected'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* User Profile */}
        <SidebarUserProfile
          name={user?.name}
          tenantName={user?.tenantName}
          onSignOut={handleSignOut}
        />
       
      </div>
    </aside>
      {/* Main Content */}
      <div className=" w-[calc(100%-16.5rem)]">
        {/* Header */}
       {location.pathname=="/dashboard" && 
         <header className="h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-6">
           <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
             {navigation.find((item) => location.pathname.startsWith(item.path))?.name ||
               'Resolve AI'}
           </h1>
           <ThemeToggle />
        </header>}

        {/* Page Content */}
        <main className="px-6 py-8  h-[calc(100%-3rem)] overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
